import { confirmToast } from "../../utils/toast";
import React, { useEffect, useState } from 'react';
import axios from "../../utils/axiosConfig";

const BankMaster = () => {
  const initialForm = {
    bankName: '',
    branch: '',
    swiftCode: '',
    address: '',
    city: '',
    state: '',
    country: '',
    pinCode: '',
    remarks: ''
  };
  const [activeTab, setActiveTab] = useState("list");

  const [form, setForm] = useState(initialForm);
  const [banks, setBanks] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(5);
  useEffect(() => {
    fetchBanks();
    fetchLocations();
  }, []);

  const fetchBanks = async () => {
    try {
      const res = await axios.get("/banks");
      setBanks(res.data);
    } catch (err) {
      console.error('Failed to load banks', err);
      alert('Failed to load bank list');
    }
  };

  const fetchLocations = async () => {
    try {
      const [countryResponse, stateResponse] = await Promise.all([
        axios.get("/countries"),
        axios.get("/states")
      ]);
      setCountries(countryResponse.data);
      setStates(stateResponse.data);
    } catch (err) {
      console.error("Failed to load countries and states", err);
      alert("Failed to load country and state lists");
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCountryChange = (e) => {
    setForm({
      ...form,
      country: e.target.value,
      state: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        BankName: form.bankName,
        Branch: form.branch,
        SwiftCode: form.swiftCode,
        Address: form.address,
        City: form.city,
        State: form.state,      // kept for DB, even if not shown
        Country: form.country,  // kept for DB
        PinCode: form.pinCode,
        Remarks: form.remarks
      };

      if (editingId) {
        await axios.put(`${"/banks"}/${editingId}`, payload);
      } else {
        await axios.post("/banks", payload);
      }

      alert(editingId ? "Bank updated successfully" : "Bank saved successfully");
      resetForm();
      await fetchBanks();
      setActiveTab("list");
    } catch (error) {
      console.error(error);
      alert('❌ Failed to save: ' + error.message);
    }
  };

  const handleRowClick = (bank) => {
    const country = countries.find((item) =>
      String(item.CountryName) === String(bank.Country) ||
      String(item.CountryId) === String(bank.Country)
    );
    const state = states.find((item) =>
      String(item.StateName) === String(bank.State) ||
      String(item.StateId) === String(bank.State)
    );

    setForm({
      bankName: bank.BankName || '',
      branch: bank.Branch || '',
      swiftCode: bank.SwiftCode || '',
      address: bank.Address || '',
      city: bank.City || '',
      state: state?.StateName || bank.State || '',
      country: country?.CountryName || bank.Country || '',
      pinCode: bank.PinCode || '',
      remarks: bank.Remarks || ''
    });
    setEditingId(bank.BankID);
  };

  const handleDelete = async (id) => {
    if (!await confirmToast('Are you sure you want to delete this bank?')) return;
    try {
      await axios.delete(`${"/banks"}/${id}`);
      fetchBanks();
      if (editingId === id) resetForm();
    } catch (err) {
      console.error(err);
      alert('Delete failed');
    }
  };

  const resetForm = () => {
    setForm(initialForm);
    setEditingId(null);
  };

  // client-side filter for search box (bank name / branch / swift / address / city)
  const filteredBanks = banks.filter((b) => {
    const t = search.toLowerCase();
    if (!t) return true;
    return (
      (b.BankName || '').toLowerCase().includes(t) ||
      (b.Branch || '').toLowerCase().includes(t) ||
      (b.SwiftCode || '').toLowerCase().includes(t) ||
      (b.Address || '').toLowerCase().includes(t) ||
      (b.City || '').toLowerCase().includes(t)
    );
  });

  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;

  const currentBanks = filteredBanks.slice(indexOfFirst, indexOfLast);

  const totalPages = Math.ceil(filteredBanks.length / rowsPerPage);

  const selectedCountry = countries.find((country) =>
    String(country.CountryName) === String(form.country) ||
    String(country.CountryId) === String(form.country)
  );

  const availableStates = selectedCountry
    ? states.filter((state) => String(state.CountryId) === String(selectedCountry.CountryId))
    : [];

  return (
    <div className="container mt-3">
      <div className="mb-3">
        <h4 className="fw-bold">Bank Master</h4>
      </div>
      <div className="card shadow-sm border-0">

        {/* HEADER WITH TABS */}
        <div className="card-header bg-white">
          <ul className="nav nav-tabs card-header-tabs">
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === "list" ? "active" : ""}`}
                onClick={() => setActiveTab("list")}
              >
                Bank List
              </button>
            </li>

            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === "form" ? "active" : ""}`}
                onClick={() => setActiveTab("form")}
              >
                {editingId ? "Edit Bank" : "Add Bank"}
              </button>
            </li>
          </ul>
        </div>

        {/* BODY */}
        <div className="card-body">

          {/* ================= LIST TAB ================= */}
          {activeTab === "list" && (
            <>
              <div className="d-flex justify-content-between mb-3">
                <input
                  type="text"
                  className="form-control w-50"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }} />


              </div>

              <table className="table table-bordered table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Bank Name</th>
                    <th>Branch</th>
                    <th>Swift</th>
                    <th>Address</th>
                    <th>City</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBanks.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-3">
                        No banks found.
                      </td>
                    </tr>
                  ) : (
                    currentBanks.map((bank) => (
                      <tr key={bank.BankID}>
                        <td>{bank.BankName}</td>
                        <td>{bank.Branch}</td>
                        <td>{bank.SwiftCode}</td>
                        <td>{bank.Address}</td>
                        <td>{bank.City}</td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => {
                              handleRowClick(bank);
                              setActiveTab("form");
                            }}
                          >
                            Edit
                          </button>

                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(bank.BankID)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">

                  <div>
                    Showing {indexOfFirst + 1} to{" "}
                    {Math.min(indexOfLast, filteredBanks.length)} of{" "}
                    {filteredBanks.length} entries
                  </div>

                  <nav>
                    <ul className="pagination mb-0">

                      {/* Previous */}
                      <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage - 1)}
                        >
                          Previous
                        </button>
                      </li>

                      {/* Page Numbers */}
                      {[...Array(totalPages)].map((_, index) => (
                        <li
                          key={index}
                          className={`page-item ${currentPage === index + 1 ? "active" : ""
                            }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(index + 1)}
                          >
                            {index + 1}
                          </button>
                        </li>
                      ))}

                      {/* Next */}
                      <li
                        className={`page-item ${currentPage === totalPages ? "disabled" : ""
                          }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage + 1)}
                        >
                          Next
                        </button>
                      </li>

                    </ul>
                  </nav>

                </div>
              )}

            </>
          )}

          {/* ================= FORM TAB ================= */}
          {activeTab === "form" && (
            <form onSubmit={handleSubmit}>
              <div className="row g-3">

                <div className="col-md-3">
                  <label className="form-label fw-semibold">Bank Name *</label>
                  <input
                    name="bankName"
                    value={form.bankName}
                    onChange={handleChange}
                    className="form-control"
                    required
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-semibold">Branch *</label>
                  <input
                    name="branch"
                    value={form.branch}
                    onChange={handleChange}
                    className="form-control"
                    required
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-semibold">Swift Code</label>
                  <input
                    name="swiftCode"
                    value={form.swiftCode}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Bank Address</label>
                  <textarea
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                    className="form-control"
                    rows="2"
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-semibold">City</label>
                  <input
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-semibold">Country</label>
                  <select
                    name="country"
                    value={form.country}
                    onChange={handleCountryChange}
                    className="form-select"
                  >
                    <option value="">-- Select Country --</option>
                    {countries.map((country) => (
                      <option key={country.CountryId} value={country.CountryName}>
                        {country.CountryName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-semibold">State</label>
                  <select
                    name="state"
                    value={form.state}
                    onChange={handleChange}
                    className="form-select"
                    disabled={!form.country}
                  >
                    <option value="">-- Select State --</option>
                    {availableStates.map((state) => (
                      <option key={state.StateId} value={state.StateName}>
                        {state.StateName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-semibold">Pin Code</label>
                  <input
                    name="pinCode"
                    value={form.pinCode}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>

                <div className="col-12">
                  <label className="form-label fw-semibold">Remarks</label>
                  <textarea
                    name="remarks"
                    value={form.remarks}
                    onChange={handleChange}
                    className="form-control"
                    rows="3"
                  />
                </div>

              </div>

              <div className="mt-4 text-end">
                <button type="submit" className="btn btn-success me-2">
                  {editingId ? "Update" : "Save"}
                </button>

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setActiveTab("list")}
                >
                  Back to List
                </button>
              </div>
            </form>
          )}

        </div>
      </div>
    </div>
  );

};

export default BankMaster;
